import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { CRM_CONTACT_LIST_SEARCH } from '../contacts.queries';
import {
  CONSENT_FILTERS,
  ConsentKey,
  ContactListState,
  MAX_SET_IDS,
  NO_COUNTRY,
  SUPPORT_CHOICES,
} from '../contactList';
import {
  CONTACTED_PRESETS,
  countryChoices,
  countryLabel,
  JOINED_PRESETS,
  SOURCE_CHOICES,
} from './filterModel';
import { DateRangeEditor } from './DateRangeEditor';
import { OptionItem, OptionList } from './OptionList';
import { ConsentCounts, FacetOption, FilterOptions, ListOption } from './types';

type Change = (change: Partial<ContactListState>) => void;

const n = (value: number) => value.toLocaleString('en-US');

/** Adds or removes one value. */
const toggled = <T,>(values: ReadonlyArray<T>, value: T) =>
  values.includes(value) ? values.filter((other) => other !== value) : [...values, value];

/** A compact segmented control, used for every either-or choice in the panels. */
const Segmented = <T extends string>({
  label,
  value,
  choices,
  onChange,
  fullWidth,
}: {
  label: string;
  value: T;
  choices: Array<{ value: T; label: React.ReactNode }>;
  onChange: (value: T) => void;
  fullWidth?: boolean;
}) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    color="primary"
    fullWidth={fullWidth}
    value={value}
    aria-label={label}
    // Clicking the chosen one again sends null: keep the choice.
    onChange={(_event, next: T | null) => next !== null && onChange(next)}
    sx={{
      bgcolor: '#f1f5f9',
      p: 0.375,
      borderRadius: 2,
      gap: 0.375,
      '& .MuiToggleButton-root': {
        border: 0,
        borderRadius: '6px !important',
        px: 1.25,
        py: 0.375,
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: 'text.secondary',
        whiteSpace: 'nowrap',
        '&.Mui-selected': {
          bgcolor: 'background.paper',
          color: 'primary.dark',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
          '&:hover': { bgcolor: 'background.paper' },
        },
      },
    }}
  >
    {choices.map((choice) => (
      <ToggleButton key={choice.value} value={choice.value}>
        {choice.label}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

const Hint = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
    {children}
  </Typography>
);

/* ------------------------------------------------------------------ *
 * Tags
 * ------------------------------------------------------------------ */

export const TagsEditor = ({ state, options, onChange }: { state: ContactListState; options?: FilterOptions; onChange: Change }) => {
  const tags = options?.tags ?? [];
  const known = new Set(tags.map((tag) => tag.id));
  const items: Array<OptionItem> = [
    ...[...tags]
      .sort((a, b) => b.contactCount - a.contactCount)
      .map((tag) => ({
        value: String(tag.id),
        label: tag.name,
        count: tag.contactCount,
        color: tag.color,
        note: tag.isSystem ? 'system' : undefined,
      })),
    // Named in the URL but unknown to the API: deleted, or another environment's.
    ...[...state.tagIds, ...state.excludeTagIds]
      .filter((id) => !known.has(id))
      .map((id) => ({ value: String(id), label: `Tag #${id}`, color: null })),
  ];
  const full = state.tagIds.length >= MAX_SET_IDS;

  const include = (value: string) => {
    const id = Number(value);
    if (!state.tagIds.includes(id) && full) return;
    onChange({ tagIds: toggled(state.tagIds, id), excludeTagIds: state.excludeTagIds.filter((other) => other !== id) });
  };
  const leaveOut = (value: string) => {
    const id = Number(value);
    if (!state.excludeTagIds.includes(id) && state.excludeTagIds.length >= MAX_SET_IDS) return;
    onChange({ excludeTagIds: toggled(state.excludeTagIds, id), tagIds: state.tagIds.filter((other) => other !== id) });
  };

  return (
    <OptionList
      label="Tags"
      items={items}
      selected={state.tagIds.map(String)}
      excluded={state.excludeTagIds.map(String)}
      onToggle={include}
      onToggleExclude={leaveOut}
      searchPlaceholder="Find a tag"
      loading={!options}
      emptyText={options ? 'No tag has that name.' : 'Loading tags…'}
      footer={
        <>
          {state.tagIds.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                People with
              </Typography>
              <Segmented
                label="How the tags combine"
                value={state.tagMatch}
                choices={[
                  { value: 'any', label: 'Any of them' },
                  { value: 'all', label: 'All of them' },
                ]}
                onChange={(tagMatch) => onChange({ tagMatch })}
              />
            </Box>
          )}
          <Hint>{full ? `At most ${MAX_SET_IDS} tags at a time.` : 'Tick a tag to include it. ⊘ leaves out people who have it.'}</Hint>
        </>
      }
    />
  );
};

/* ------------------------------------------------------------------ *
 * Lists
 * ------------------------------------------------------------------ */

/**
 * The first request holds the 200 largest lists. When there are more, typing
 * searches the rest by name (crmSegments(search)). Names seen either way go in
 * `known`, which the bar's pill reads too.
 */
export const ListsEditor = ({
  state,
  options,
  known,
  onChange,
}: {
  state: ContactListState;
  options?: FilterOptions;
  known: Map<number, ListOption>;
  onChange: Change;
}) => {
  const [query, setQuery] = useState('');
  const [asked, setAsked] = useState('');
  const searchApi = Boolean(options && options.listTotal > options.lists.length);

  useEffect(() => {
    const timer = setTimeout(() => setAsked(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, loading } = useQuery<{ crmSegments: { items: Array<ListOption> } }>(CRM_CONTACT_LIST_SEARCH, {
    variables: { search: asked },
    skip: !searchApi || !asked,
  });
  data?.crmSegments.items.forEach((list) => known.set(list.id, list));

  const found = searchApi && asked ? data?.crmSegments.items ?? [] : options?.lists ?? [];
  const chosen = state.listIds.map((id) => known.get(id) ?? { id, name: `List #${id}`, contactCount: 0 });
  const seen = new Set(found.map((list) => list.id));
  const items: Array<OptionItem> = [...chosen.filter((list) => !seen.has(list.id)), ...found].map((list) => ({
    value: String(list.id),
    label: list.name,
    count: list.contactCount,
  }));

  return (
    <OptionList
      label="Lists"
      items={items}
      selected={state.listIds.map(String)}
      onToggle={(value) => {
        const id = Number(value);
        if (!state.listIds.includes(id) && state.listIds.length >= MAX_SET_IDS) return;
        onChange({ listIds: toggled(state.listIds, id) });
      }}
      searchPlaceholder={searchApi ? 'Find any list by name' : 'Find a list'}
      query={searchApi ? query : undefined}
      onQueryChange={searchApi ? setQuery : undefined}
      loading={!options || (searchApi && (loading || query.trim() !== asked))}
      emptyText={options ? 'No list has that name.' : 'Loading lists…'}
      footer={
        <Hint>
          People on any of the ticked lists.
          {searchApi && ` The ${n(options?.lists.length ?? 0)} largest of ${n(options?.listTotal ?? 0)} are shown; type to find the rest.`}
        </Hint>
      }
    />
  );
};

/* ------------------------------------------------------------------ *
 * Country
 * ------------------------------------------------------------------ */

export const CountryEditor = ({ state, options, onChange }: { state: ContactListState; options?: FilterOptions; onChange: Change }) => {
  // The facet's null bucket is "no country", chosen as NO_COUNTRY (hasCountry: false).
  const counts = new Map((options?.countries ?? []).map((country) => [country.value ?? NO_COUNTRY, country.count]));
  const codes = countryChoices(counts);
  const every = codes.filter((code) => code !== NO_COUNTRY);
  const chosen = [...(state.noCountry ? [NO_COUNTRY] : []), ...state.countries];

  const items: Array<OptionItem> = codes.map((code) => {
    const count = counts.get(code) ?? 0;
    return {
      value: code,
      label: countryLabel(code),
      note: code === NO_COUNTRY ? undefined : code,
      count: count > 0 ? count : undefined,
      group: count > 0 ? 'With contacts' : 'Every other country',
    };
  });

  return (
    <>
      <Segmented
        label="In or outside these countries"
        fullWidth
        value={state.countryMatch}
        choices={[
          { value: 'in', label: 'In these countries' },
          { value: 'out', label: 'Not in these' },
        ]}
        onChange={(countryMatch) => onChange({ countryMatch })}
      />
      <Box sx={{ display: 'flex', gap: 0.5, my: 1 }}>
        <Button size="small" onClick={() => onChange({ countries: every })} disabled={state.countries.length === every.length}>
          Select all countries
        </Button>
        {chosen.length > 0 && (
          <Button size="small" color="inherit" onClick={() => onChange({ countries: [], noCountry: false, countryMatch: 'in' })}>
            Clear countries
          </Button>
        )}
      </Box>
      <OptionList
        label="Countries"
        items={items}
        selected={chosen}
        onToggle={(code) => {
          if (code === NO_COUNTRY) onChange({ noCountry: !state.noCountry });
          else onChange({ countries: toggled(state.countries, code) });
        }}
        searchPlaceholder="Find a country"
        limit={400}
        maxHeight={300}
        emptyText="No country has that name."
      />
      {state.countries.length > 0 && !state.noCountry && state.countryMatch === 'out' && (
        <Hint>Includes contacts with no country.</Hint>
      )}
    </>
  );
};

/* ------------------------------------------------------------------ *
 * Consent
 * ------------------------------------------------------------------ */

/** What each gate means, worked out by the server (§1). */
const CONSENT_MEANINGS: Record<ConsentKey, string> = {
  canEmail: 'Opted in, address works, not do-not-contact',
  canCall: 'Has a phone, not do-not-call or do-not-contact',
  canText: 'Opted in to texts, has a phone, not do-not-contact',
  doNotCall: 'Flagged: never phone them',
  doNotContact: 'Flagged: no outreach of any kind',
};

export const ConsentEditor = ({
  state,
  counts,
  onChange,
}: {
  state: ContactListState;
  counts?: ConsentCounts;
  onChange: Change;
}) => (
  <Box sx={{ display: 'grid', gap: 0.5 }}>
    {CONSENT_FILTERS.map(({ key, label }) => {
      const current = state.consent[key];
      return (
        <Box
          key={key}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-of-type': { borderBottom: 0 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {label}
              {counts && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {n(counts[key])}
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {CONSENT_MEANINGS[key]}
            </Typography>
          </Box>
          <Segmented
            label={label}
            value={current === undefined ? 'any' : current ? 'yes' : 'no'}
            choices={[
              { value: 'any', label: 'Any' },
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ]}
            onChange={(choice) => {
              const consent = { ...state.consent };
              if (choice === 'any') delete consent[key];
              else consent[key] = choice === 'yes';
              onChange({ consent });
            }}
          />
        </Box>
      );
    })}
    {counts && <Hint>The numbers count the contacts listed now.</Hint>}
  </Box>
);

/* ------------------------------------------------------------------ *
 * Joined, last contacted
 * ------------------------------------------------------------------ */

const Dot = () => (
  <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', ml: 0.75 }} />
);

export const JoinedEditor = ({ state, today, onChange }: { state: ContactListState; today: string; onChange: Change }) => {
  const crmSet = Boolean(state.joined.from || state.joined.to);
  const appSet = Boolean(state.appJoined.from || state.appJoined.to || state.appMember !== undefined);
  const [tab, setTab] = useState<'crm' | 'app'>(appSet && !crmSet ? 'app' : 'crm');

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Segmented
          label="Joined what"
          fullWidth
          value={tab}
          choices={[
            { value: 'crm', label: <>The CRM{crmSet && <Dot />}</> },
            { value: 'app', label: <>The app{appSet && <Dot />}</> },
          ]}
          onChange={setTab}
        />
      </Box>

      {tab === 'crm' ? (
        <DateRangeEditor
          key="crm"
          label="Joined the CRM"
          range={state.joined}
          presets={JOINED_PRESETS}
          today={today}
          onChange={(joined) => onChange({ joined })}
          hint="Their NationBuilder sign-up if they came from there, else when they were added."
        />
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              App account
            </Typography>
            <Segmented
              label="App account"
              value={state.appMember === undefined ? 'any' : state.appMember ? 'yes' : 'no'}
              choices={[
                { value: 'any', label: 'Any' },
                { value: 'yes', label: 'Has the app' },
                { value: 'no', label: 'No app account' },
              ]}
              onChange={(choice) =>
                // No account has no account date, so "none" drops the dates.
                onChange(choice === 'no' ? { appMember: false, appJoined: {} } : { appMember: choice === 'any' ? undefined : true })
              }
            />
          </Box>
          <DateRangeEditor
            key="app"
            label="Joined the app"
            range={state.appJoined}
            presets={JOINED_PRESETS}
            today={today}
            disabled={state.appMember === false}
            onChange={(appJoined) => onChange({ appJoined })}
            hint="When their app account was made. Only people with an account match a date."
          />
        </>
      )}
    </>
  );
};

export const ContactedEditor = ({ state, today, onChange }: { state: ContactListState; today: string; onChange: Change }) => (
  <DateRangeEditor
    label="Last contacted"
    range={state.contacted}
    presets={CONTACTED_PRESETS}
    today={today}
    onChange={(contacted) => onChange({ contacted, neverContacted: false })}
    never={{
      label: 'Never contacted',
      active: state.neverContacted,
      onChange: (neverContacted) => onChange({ neverContacted, contacted: {} }),
    }}
    hint="The last time anyone reached them, by any channel. “Over 90 days ago” and “Over a year ago” leave out people never contacted."
  />
);

/* ------------------------------------------------------------------ *
 * Support level, point person, source, stage
 * ------------------------------------------------------------------ */

/** NationBuilder's scale, from support to opposition. */
const SUPPORT_COLOURS: Record<string, string | null> = {
  '1-2': '#16a34a',
  '1': '#15803d',
  '2': '#4ade80',
  '3': '#f59e0b',
  '4': '#fb923c',
  '5': '#dc2626',
  '4-5': '#dc2626',
  none: null,
};

export const SupportEditor = ({
  state,
  levels,
  onChange,
}: {
  state: ContactListState;
  levels?: Array<FacetOption>;
  onChange: Change;
}) => {
  const countOf = (choice: string) => {
    if (!levels) return undefined;
    if (choice === 'none') return levels.find((level) => level.value === null)?.count ?? 0;
    const [min, max]: Array<number> = choice.split('-').map(Number);
    return levels
      .filter((level) => level.value !== null && Number(level.value) >= min && Number(level.value) <= (max ?? min))
      .reduce((sum, level) => sum + level.count, 0);
  };

  const items: Array<OptionItem> = [
    { value: 'any', label: 'Any' },
    ...SUPPORT_CHOICES.map((choice) => ({
      value: choice.value,
      label: choice.label,
      count: countOf(choice.value),
      color: SUPPORT_COLOURS[choice.value] ?? null,
    })),
  ];

  return (
    <OptionList
      label="Support level"
      single
      items={items}
      selected={[state.support ?? 'any']}
      onToggle={(value) => onChange({ support: value === 'any' ? null : value })}
      maxHeight={400}
      footer={<Hint>NationBuilder's scale: 1 is strong support, 5 strong opposition.</Hint>}
    />
  );
};

/** The facet's null bucket, and the "no point person" choice. */
const NO_OWNER = 'none';

export const OwnerEditor = ({
  state,
  owners,
  onChange,
}: {
  state: ContactListState;
  owners?: Array<FacetOption>;
  onChange: Change;
}) => {
  const listed = (owners ?? []).filter((owner) => owner.value !== null);
  const seen = new Set(listed.map((owner) => owner.value));
  const items: Array<OptionItem> = [
    { value: NO_OWNER, label: 'No point person', count: owners?.find((owner) => owner.value === null)?.count },
    ...listed.map((owner) => ({ value: owner.value as string, label: owner.label ?? `User #${owner.value}`, count: owner.count })),
    // Chosen in a link, though nobody has them now.
    ...state.ownerIds.filter((id) => !seen.has(String(id))).map((id) => ({ value: String(id), label: `User #${id}` })),
  ];

  return (
    <OptionList
      label="Point people"
      items={items}
      selected={state.noOwner ? [NO_OWNER] : state.ownerIds.map(String)}
      onToggle={(value) => {
        // "No point person" stands alone: picking it clears the names, and a name clears it.
        if (value === NO_OWNER) onChange({ noOwner: !state.noOwner, ownerIds: [] });
        else onChange({ noOwner: false, ownerIds: toggled(state.ownerIds, Number(value)) });
      }}
      searchPlaceholder="Find a person"
      loading={!owners}
      emptyText={owners ? 'Nobody has that name.' : 'Loading point people…'}
    />
  );
};

export const SourceEditor = ({
  state,
  sources,
  onChange,
}: {
  state: ContactListState;
  sources?: Array<FacetOption>;
  onChange: Change;
}) => (
  <OptionList
    label="Sources"
    items={SOURCE_CHOICES.map((choice) => ({
      ...choice,
      count: sources ? sources.find((source) => source.value === choice.value)?.count ?? 0 : undefined,
    }))}
    selected={state.sources}
    onToggle={(value) => onChange({ sources: toggled(state.sources, value) })}
    footer={<Hint>Where the record came from.</Hint>}
  />
);

export const StageEditor = ({
  state,
  stages,
  onChange,
}: {
  state: ContactListState;
  stages?: Array<FacetOption>;
  onChange: Change;
}) => {
  const listed = (stages ?? []).filter((stage) => stage.value !== null);
  const seen = new Set(listed.map((stage) => stage.value));
  const items: Array<OptionItem> = [
    ...listed.map((stage) => ({ value: stage.value as string, label: stage.value as string, count: stage.count })),
    ...state.stages.filter((stage) => !seen.has(stage)).map((stage) => ({ value: stage, label: stage })),
  ];

  return (
    <OptionList
      label="Stages"
      items={items}
      selected={state.stages}
      onToggle={(value) => onChange({ stages: toggled(state.stages, value) })}
      searchPlaceholder="Find a stage"
      loading={!stages}
      emptyText={stages ? 'No stage has that name.' : 'Loading stages…'}
    />
  );
};
